import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { usePageParams } from 'hooks/use-page-params';
import fileDownload from 'js-file-download';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { resolveContentDispositionFilename } from './resolve-content-disposition-filename';

const endpoint = 'ums/v1/cms/exclusive-deals/sheet/download';
export const downloadDealsSheetEndpointIdentifier = endpoint;

type TData = void;
type TError = AxiosError;
type TQueryKey = [string, { countryCode: unknown; isActive: unknown; supplierId: number | null }];
type Options = Omit<UseQueryOptions<void, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Download the exclusive deals Excel sheet (also used as the upload template).
 * - `supplierId` omitted → all deals (one row per deal), respecting the list filters.
 * - `supplierId` provided → that supplier's full public catalog (one row per product)
 *   with the active deal backfilled.
 * Disabled by default — trigger via refetch().
 */
export function useDownloadDealsSheetApi(supplierId?: number | null, options?: Options): ReturnType {
  const toast = useCustomToast();
  const { getPageParams } = usePageParams();
  const { countryCode, isActive } = getPageParams();
  const normalizedSupplierId = supplierId ?? null;

  const queryFn = async () => {
    try {
      const params: Record<string, unknown> = { countryCode, isActive };
      if (normalizedSupplierId != null) params.supplierId = normalizedSupplierId;

      const response = await http.get(endpoint, {
        responseType: 'arraybuffer',
        params,
      });

      const today = new Date().toISOString().split('T')[0];
      const fallback = normalizedSupplierId != null
        ? `exclusive-deals-supplier-${normalizedSupplierId}-${today}.xlsx`
        : `exclusive-deals-${today}.xlsx`;
      const filename = resolveContentDispositionFilename(response.headers?.['content-disposition'], fallback);
      fileDownload(response.data, filename);
    } catch (error: any) {
      // Important to not show the toast in unauthorized case.
      if (!error.response?.status || error.response?.status === 401) throw error;

      // responseType is arraybuffer, so error.response.data is binary — show a generic message.
      toast({
        description:
          error.response?.status === 404
            ? 'Supplier not found'
            : 'Failed to download the exclusive deals sheet',
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, { countryCode, isActive, supplierId: normalizedSupplierId }], queryFn, {
    enabled: false,
    ...options,
  });
}
